/**
 * Modals, built on `<dialog>`.
 *
 * `prompt()` was doing this job and doing it badly: it is a chrome-styled box
 * that ignores the page entirely, it blocks the whole tab while it is up, it
 * cannot show a second field or explain what a name is for, and on a form
 * where the answer matters — what to call an album, who somebody is — it gives
 * the user no way to see what they are naming.
 *
 * `<dialog showModal()>` is used rather than a hand-rolled overlay because the
 * browser already implements the parts that are tedious and easy to get wrong:
 * focus is trapped inside, the rest of the page is inert to assistive
 * technology, Escape closes, and it sits in the top layer so no stacking
 * context can bury it.
 */

import { h, replace } from "./dom";
import { icon } from "./icons";

export interface Field {
  name: string;
  label: string;
  value?: string;
  placeholder?: string;
  /** Rendered under the input, for the thing a label is too short to say. */
  hint?: string;
  multiline?: boolean;
  inputType?: "text" | "date";
  /**
   * A choice rather than free text: `[value, label]` pairs.
   *
   * For the questions where typing the answer would be typing an id — "which
   * of these people is this the same as" is a list of people, and asking
   * somebody to write a UUID is asking them to get it wrong.
   */
  options?: Array<[string, string]>;
  required?: boolean;
  maxLength?: number;
}

export interface AskOptions {
  title: string;
  /** A sentence above the fields. Say why, not what. */
  body?: string;
  fields: Field[];
  confirm?: string;
  cancel?: string;
  /** Marks the confirm button as doing something that is hard to undo. */
  destructive?: boolean;
  /** Shown beside the fields — the album cover, the face being named. */
  preview?: HTMLElement;
}

export interface PersonChoice {
  value: string;
  label: string;
  detail?: string;
  image?: string | null;
}

export interface OptionChoice {
  value: string;
  label: string;
  detail?: string;
  icon?: string;
}

/** A compact command menu presented as a modal for mouse, touch and keyboard. */
export function pickOption(opts: {
  title: string;
  choices: OptionChoice[];
  selected?: string;
}): Promise<string | null> {
  return new Promise((resolve) => {
    const dialog = h("dialog", { class: "modal option-picker" });
    let settled = false;
    const finish = (value: string | null): void => {
      if (settled) return;
      settled = true;
      resolve(value);
      dialog.close();
      dialog.remove();
    };
    replace(
      dialog,
      h(
        "div",
        { class: "modal-body" },
        h("h2", { class: "modal-title", text: opts.title }),
        h(
          "div",
          { class: "option-pick-list" },
          ...opts.choices.map((choice) =>
            h(
              "button",
              {
                class: choice.value === opts.selected ? "option-pick on" : "option-pick",
                type: "button",
                onclick: () => finish(choice.value),
              },
              icon(choice.icon ?? "chevron", "i sm"),
              h(
                "span",
                { class: "option-pick-copy" },
                h("strong", { text: choice.label }),
                choice.detail ? h("small", { text: choice.detail }) : null,
              ),
              choice.value === opts.selected ? h("span", { class: "option-current", text: "Current" }) : null,
            ),
          ),
        ),
        h(
          "div",
          { class: "modal-actions" },
          h("button", { class: "btn ghost", onclick: () => finish(null) }, "Cancel"),
        ),
      ),
    );
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      finish(null);
    });
    document.body.appendChild(dialog);
    dialog.showModal();
    dialog.querySelector<HTMLButtonElement>(".option-pick.on, .option-pick")?.focus();
  });
}

/** Search a large people list, selecting one person or any number of them. */
export function pickPeople(opts: {
  title: string;
  choices: PersonChoice[];
  selected?: string[];
  multiple?: boolean;
  confirm?: string;
}): Promise<string[] | null> {
  return new Promise((resolve) => {
    const dialog = h("dialog", { class: "modal person-picker" });
    const chosen = new Set(opts.selected ?? []);
    const list = h("div", { class: "person-pick-list" });
    const search = h("input", {
      class: "person-pick-search",
      type: "search",
      placeholder: "Search people",
      "aria-label": "Search people",
    }) as HTMLInputElement;
    let settled = false;

    const finish = (result: string[] | null): void => {
      if (settled) return;
      settled = true;
      resolve(result);
      dialog.close();
      dialog.remove();
    };
    const paint = (): void => {
      const query = search.value.trim().toLocaleLowerCase();
      const visible = opts.choices.filter((choice) =>
        `${choice.label} ${choice.detail ?? ""}`.toLocaleLowerCase().includes(query),
      );
      replace(
        list,
        ...visible.map((choice) => {
          const selected = chosen.has(choice.value);
          return h(
            "button",
            {
              class: selected ? "person-pick on" : "person-pick",
              type: "button",
              "aria-pressed": String(selected),
              onclick: () => {
                if (!opts.multiple) {
                  finish([choice.value]);
                  return;
                }
                if (selected) chosen.delete(choice.value);
                else chosen.add(choice.value);
                paint();
              },
            },
            choice.image
              ? h("img", { src: choice.image, alt: "", loading: "lazy" })
              : h("span", { class: "person-pick-empty" }, icon("people", "i sm")),
            h(
              "span",
              { class: "person-pick-copy" },
              h("strong", { text: choice.label }),
              choice.detail ? h("small", { text: choice.detail }) : null,
            ),
            opts.multiple
              ? h("span", { class: "person-pick-check", "aria-hidden": "true" }, selected ? "✓" : "")
              : icon("chevron", "i sm"),
          );
        }),
      );
      if (!visible.length) list.appendChild(h("p", { class: "modal-hint", text: "No people match." }));
    };
    search.addEventListener("input", paint);
    replace(
      dialog,
      h(
        "div",
        { class: "modal-body" },
        h("h2", { class: "modal-title", text: opts.title }),
        search,
        list,
        h(
          "div",
          { class: "modal-actions" },
          h("button", { class: "btn ghost", onclick: () => finish(null) }, "Cancel"),
          opts.multiple
            ? h(
                "button",
                { class: "btn primary", onclick: () => finish([...chosen]) },
                opts.confirm ?? "Apply",
              )
            : null,
        ),
      ),
    );
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      finish(null);
    });
    document.body.appendChild(dialog);
    dialog.showModal();
    paint();
    search.focus();
  });
}

/**
 * Ask for one or more values. Resolves with them, or null if dismissed.
 *
 * Dismissal is deliberately indistinguishable from cancelling: Escape, the
 * backdrop and the cancel button all mean "I did not want this", and a caller
 * that has to tell them apart is a caller doing something surprising.
 */
export function ask(opts: AskOptions): Promise<Record<string, string> | null> {
  return new Promise((resolve) => {
    const dialog = h("dialog", { class: "modal" });
    let settled = false;

    const finish = (value: Record<string, string> | null): void => {
      if (settled) return;
      settled = true;
      resolve(value);
      dialog.close();
      // Removed on close rather than on resolve, so the browser's own close
      // animation is not interrupted by the node vanishing.
      dialog.addEventListener("close", () => dialog.remove(), { once: true });
      if (!dialog.open) dialog.remove();
    };

    const inputs = opts.fields.map((f) => {
      if (f.options) {
        const sel = h(
          "select",
          { name: f.name },
          ...f.options.map(([value, text]) =>
            h("option", { value, selected: value === f.value, text }),
          ),
        );
        return sel as unknown as HTMLInputElement;
      }
      const el = f.multiline
        ? h("textarea", {
            name: f.name,
            rows: "3",
            placeholder: f.placeholder ?? "",
            maxlength: f.maxLength ? String(f.maxLength) : "",
          })
        : h("input", {
            name: f.name,
          type: f.inputType ?? "text",
            value: f.value ?? "",
            placeholder: f.placeholder ?? "",
            maxlength: f.maxLength ? String(f.maxLength) : "",
            autocomplete: "off",
          });
      if (f.multiline && f.value) (el as HTMLTextAreaElement).value = f.value;
      return el as HTMLInputElement | HTMLTextAreaElement;
    });

    const submit = (): void => {
      const out: Record<string, string> = {};
      for (let i = 0; i < opts.fields.length; i++) {
        const field = opts.fields[i]!;
        const value = inputs[i]!.value.trim();
        if (field.required && !value) {
          inputs[i]!.focus();
          inputs[i]!.classList.add("bad");
          return;
        }
        out[field.name] = value;
      }
      finish(out);
    };

    const form = h(
      "form",
      { class: "modal-form", method: "dialog" },
      ...opts.fields.map((f, i) => {
        const control = f.options
          ? h("span", { class: "modal-select-wrap" }, inputs[i]!, icon("chevron", "i sm"))
          : inputs[i]!;
        return h(
          "label",
          { class: "modal-field" },
          h("span", { class: "modal-label" }, f.label),
          control,
          ...(f.hint ? [h("span", { class: "modal-hint" }, f.hint)] : []),
        );
      }),
    );
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      submit();
    });

    replace(
      dialog,
      h(
        "div",
        { class: "modal-body" },
        h("h2", { class: "modal-title" }, opts.title),
        ...(opts.body ? [h("p", { class: "modal-say" }, opts.body)] : []),
        ...(opts.preview ? [h("div", { class: "modal-preview" }, opts.preview)] : []),
        form,
        h(
          "div",
          { class: "modal-actions" },
          h(
            "button",
            { type: "button", class: "btn ghost", onclick: () => finish(null) },
            opts.cancel ?? "Cancel",
          ),
          h(
            "button",
            {
              type: "button",
              class: `btn ${opts.destructive ? "danger" : "primary"}`,
              onclick: submit,
            },
            opts.confirm ?? "Save",
          ),
        ),
      ),
    );

    // Clicking the backdrop. The dialog's own box is a child, so a click that
    // lands on the dialog element itself came from outside the box.
    dialog.addEventListener("click", (e) => {
      if (e.target === dialog) finish(null);
    });
    // Escape fires `cancel` before `close`; without this the promise never
    // settles and the caller waits for ever.
    dialog.addEventListener("cancel", (e) => {
      e.preventDefault();
      finish(null);
    });

    document.body.appendChild(dialog);
    dialog.showModal();
    inputs[0]?.focus();
    if (inputs[0] instanceof HTMLInputElement) inputs[0].select();
  });
}

/** A yes/no question. Resolves true only on the confirm button. */
export function confirmAsk(opts: {
  title: string;
  body?: string;
  confirm?: string;
  cancel?: string;
  destructive?: boolean;
}): Promise<boolean> {
  return ask({ ...opts, fields: [] }).then((r) => r !== null);
}

/**
 * A modal that reports on work rather than asking for anything.
 *
 * Returned handle stays usable after `close()`, so a caller does not have to
 * guard every progress update against a user who dismissed the box.
 */
export function progressModal(title: string, onStop?: () => void): {
  update(line: string, done: number, total: number): void;
  fail(line: string): void;
  close(): void;
} {
  const bar = h("div", { class: "modal-bar-fill" });
  const label = h("p", { class: "modal-say" }, "Starting…");
  // A pass over a whole archive can take a long time, and somebody who wants
  // their machine back should not have to close the tab to get it.
  const stop = onStop
    ? h("div", { class: "modal-actions" },
        h("button", {
          class: "btn ghost",
          onclick: () => {
            label.textContent = "stopping after this one…";
            onStop();
          },
        }, "Stop"))
    : null;
  const errors = h("ul", { class: "modal-errors" });
  const dialog = h(
    "dialog",
    { class: "modal" },
    h(
      "div",
      { class: "modal-body" },
      h("h2", { class: "modal-title" }, title),
      label,
      h("div", { class: "modal-bar" }, bar),
      errors,
      ...(stop ? [stop] : []),
    ),
  );
  document.body.appendChild(dialog);
  dialog.showModal();
  // No Escape and no backdrop dismissal: this box closes when the work it is
  // describing has finished.
  dialog.addEventListener("cancel", (e) => e.preventDefault());

  let closed = false;
  return {
    update(line, done, total) {
      if (closed) return;
      label.textContent = line;
      bar.style.width = `${total ? Math.round((done / total) * 100) : 0}%`;
    },
    fail(line) {
      if (closed) return;
      errors.appendChild(h("li", {}, line));
    },
    close() {
      if (closed) return;
      closed = true;
      dialog.close();
      dialog.remove();
    },
  };
}
